import { useEffect, useRef } from 'react';
import type { TaskState } from '../rmf-client';
import type { SiteId } from '../v1';
import { TASK_DISTANCE_IDS_PER_REQUEST, fetchTaskDistances } from '../v1-client';
import {
  adaptTaskDistanceRecord,
  getTaskDistances,
  noteTaskDistances,
  type TaskDistanceMap,
  type TaskDistanceView,
} from '../task-distances';

/**
 * At most this many requests per opening of the Tasks tab: a thousand
 * tasks, more than the live list's fortnight at NoMad's sixty a day. Past
 * it the oldest rows keep a dash -- the live stream fills in anything that
 * runs from now on, and the older view carries its own numbers.
 */
export const MAX_CATCH_UP_REQUESTS = 5;

/** How a task was when its totals were last asked for. */
type AskedAs = 'running' | 'ended';
const _askedBySite = new Map<string, Map<string, AskedAs>>();

function askedFor(site: string): Map<string, AskedAs> {
  let asked = _askedBySite.get(site);
  if (!asked) {
    asked = new Map();
    _askedBySite.set(site, asked);
  }
  return asked;
}

const ENDED: ReadonlySet<string> = new Set(['completed', 'failed', 'cancelled']);

/**
 * The live rows whose totals are worth one read, newest submission first
 * (the rows at the top of the table fill in first).
 *
 * WHAT IS NOT ASKED FOR, AND WHY:
 *  - a QUEUED task has driven nothing yet; when it starts, the stream
 *    brings its first total within about 15 s;
 *  - a task whose FINAL totals are already held -- nothing comes after;
 *  - a RUNNING task already held: the stream keeps it current;
 *  - a task already asked about in the same state this session. Asked
 *    while running and absent, it had no reading yet and the stream
 *    brings it; asked after it ended, the answer was as final as the
 *    record will ever be. An ended task asked about only while it was
 *    still running IS asked again: its finals may have arrived while
 *    this console was not listening.
 */
export function taskDistanceIdsToRead(
  tasks: readonly TaskState[],
  known: TaskDistanceMap,
  asked: ReadonlyMap<string, AskedAs>,
): string[] {
  return [...tasks]
    .sort((a, b) => (b.submittedAtMs ?? -1) - (a.submittedAtMs ?? -1))
    .filter((t) => {
      if (!t.id || t.status === 'queued') return false;
      const held: TaskDistanceView | undefined = known.get(t.id);
      if (held?.final) return false;
      const askedAs = asked.get(t.id);
      if (ENDED.has(t.status)) return askedAs !== 'ended';
      return held === undefined && askedAs === undefined;
    })
    .map((t) => t.id);
}

/**
 * Read the named tasks' totals, a request of `TASK_DISTANCE_IDS_PER_REQUEST`
 * at a time, one after another, and fold them into the store. Stops at
 * the first failure: a 404 is an api-server that predates the route (the
 * rows keep their dash), anything else is left for the next opening --
 * neither marks an id as asked, so neither is remembered as an answer.
 */
export async function readTaskDistances(site: string, tasks: readonly TaskState[]): Promise<void> {
  const asked = askedFor(site);
  const statusOf = new Map(tasks.map((t) => [t.id, t.status]));
  const ids = taskDistanceIdsToRead(tasks, getTaskDistances(site), asked);
  for (let i = 0; i < MAX_CATCH_UP_REQUESTS; i++) {
    const chunk = ids.slice(
      i * TASK_DISTANCE_IDS_PER_REQUEST,
      (i + 1) * TASK_DISTANCE_IDS_PER_REQUEST,
    );
    if (chunk.length === 0) return;
    let records;
    try {
      records = await fetchTaskDistances(site as SiteId, chunk);
    } catch {
      return;
    }
    if (records === null) return;
    noteTaskDistances(
      site,
      records.map(adaptTaskDistanceRecord).filter((v): v is TaskDistanceView => v !== null),
    );
    for (const id of chunk) asked.set(id, ENDED.has(statusOf.get(id) ?? '') ? 'ended' : 'running');
  }
}

/**
 * The Tasks tab's catch-up read for its live rows' distances: each row is
 * CONSIDERED ONCE per opening of the tab (per site), the first time the
 * tab shows it, and never on a console that does not open the tab.
 *
 * Only the opening needs it. The live stream carries every total as it
 * changes, on every console, and `useLiveData` keeps them whether or not
 * this tab is open; what it cannot carry is a total sent BEFORE this
 * console was listening -- the keys are not stored on the bus.
 *
 * PER ROW, NOT "THE FIRST NON-EMPTY LIST". The first list the tab sees is
 * not always the whole one: after a reload straight onto /tasks, a live
 * task event can seed the tasks cache with just that task before the REST
 * snapshot lands, and a read keyed on "the first rows" asked about that
 * one task and never about the finished jobs the snapshot added a moment
 * later -- they kept a dash, and a blank in the CSV, until the tab was
 * opened again. So every row the tab has not yet considered is considered
 * when it first appears. That costs nothing extra in the ordinary case:
 * a live update that only changes rows already shown adds no row, and a
 * task that is NEW while the tab is open first appears queued, which
 * `taskDistanceIdsToRead` never asks about (the stream covers it from its
 * first total). Re-reads of a row are still governed by the `asked` map.
 *
 * Nothing about the read is React state (the answers land in the store),
 * so leaving the tab mid-read is harmless and the answers still count.
 */
export function useTaskDistanceCatchUp(site: string, tasks: readonly TaskState[]): void {
  const considered = useRef<{ site: string; ids: Set<string> } | null>(null);
  useEffect(() => {
    if (!site || tasks.length === 0) return;
    if (considered.current?.site !== site) considered.current = { site, ids: new Set() };
    const seen = considered.current.ids;
    const fresh = tasks.filter((t) => t.id && !seen.has(t.id));
    if (fresh.length === 0) return;
    for (const t of fresh) seen.add(t.id);
    void readTaskDistances(site, fresh);
  }, [site, tasks]);
}

/** Tests only. */
export function _resetTaskDistanceCatchUpForTests(): void {
  _askedBySite.clear();
}
